import { useContext, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import classNames from "classnames";
import { Dropdown, Input, Menu, Space, Table, Tooltip } from "antd";
import { MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table/Table";
import { ColumnType } from "antd/lib/table";
import { SortOrder } from "antd/lib/table/interface";
import { ReactComponent as AuditSVG } from "@/assets/icons/feather-icons/activity.svg";
import { ReactComponent as PlusSVG } from "@/assets/icons/feather-icons/plus-16-white.svg";
import { AssessmentInviteStatus } from "@/modules/assessments/scenes/AssessmentInvites/invites.constants";
import { AssessmentAbilityContext } from "@/shared/contexts/ability.context";
import { AssessmentContext } from "@/shared/contexts/assessment.context";
import TableHeader from "@/shared/components/TableHeader/TableHeader";
import { statusText } from "@/shared/components/candidate/scenes/InviteCard/InviteCard";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { IAssessmentInvite } from "@/shared/hooks/initializer/use-initialize-candidate.hook";
import { longFormatDate, shortFormatDate } from "@/shared/utils/formaters/dataFormater/formatDate";
import { getLocaleDisplayName } from "@/shared/utils/locale.utils";
import { formatTableData } from "@/shared/utils/formaters/uiFormaters/tableFormaters";
import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import buttonStyle from "@/ui-components/commonComponents/Button/style.module.css";
import { ASSESSMENT_TEST_RESETS_ROUTE, REPORT_MENU_ITEM } from "@/routing/AppRouter/routes.constants";
import RenameUserModal from "@/modules/assessments/scenes/AddCandidates/components/RenameUserModal";

import style from "./style.module.css";

const candidatesInvitesColumnsStatic: ColumnsType<IAssessmentInvite> = [
  {
    key: "candidateFullName",
    title: "Candidate",
    dataIndex: "candidateFullName",
    className: style.tableColumn,
    sorter: true,
    filterIcon: (filtered: boolean) => (
      <Tooltip overlay="Search by name or email">
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      </Tooltip>
    ),
  },
  {
    key: "email",
    title: "Email",
    dataIndex: "email",
    className: style.tableColumn,
    sorter: true,
    render: (value) => <a href={`mailto:${value}`}>{value}</a>,
  },
  {
    key: "status",
    title: "Status",
    dataIndex: "statusName",
    className: style.tableColumn,
    sorter: true,
    render: (value) =>
      value === statusText[AssessmentInviteStatus.Rescinded] ? <span style={{ color: "red" }}>{value}</span> : value,
  },
  {
    key: "createdAt",
    title: "Invited On",
    dataIndex: "createdAt",
    className: style.tableColumn,
    render: (value) => formatTableData(value, shortFormatDate, longFormatDate, "-"),
    sorter: true,
  },
  {
    key: "claimedOn",
    title: "Accepted On",
    dataIndex: "claimedOn",
    className: style.tableColumn,
    render: (value) => formatTableData(value, shortFormatDate, longFormatDate, "-"),
    sorter: true,
  },
  {
    key: "startedOn",
    title: "Started On",
    dataIndex: "startedOn",
    className: style.tableColumn,
    render: (value) => formatTableData(value, shortFormatDate, longFormatDate, "-"),
    sorter: true,
  },
  {
    key: "finishedOn",
    title: "Finished On",
    dataIndex: "finishedOn",
    className: style.tableColumn,
    render: (value) => formatTableData(value, shortFormatDate, longFormatDate, "-"),
    sorter: true,
  },
];

const staticInvitedByColumn: ColumnType<IAssessmentInvite> = {
  key: "invitedBy",
  title: "Invited By",
  dataIndex: "invitedByUser",
  className: style.tableColumn,

  render: (value, { claimedByUser }) => {
    const isSelfRegistered =
      typeof claimedByUser?.userId !== "undefined" &&
      typeof value?.userId !== "undefined" &&
      claimedByUser?.userId === value?.userId;

    return value?.fullName ? (isSelfRegistered ? "Self/QR" : value?.fullName) : null;
  },
};

export function TableCandidates({
  dataList,
  onAddCandidates,
  isSendable,
  openQRModal,
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  totalCount,
  loading,
  onCandidateSearch,
  onSortChange,
  statuses,
  onFilterStatus,
  filteredStatuses,
  sortOptions,
  onInvitesRescind,
  onInvitesRescore,
  onInviteResend,
  onInvitesDisableTimer,
  onRenameCandidate,
}: {
  dataList: IAssessmentInvite[];
  onAddCandidates: () => void;
  pageSize: number;
  currentPage: number;
  totalCount: number;
  loading: boolean;
  statuses: string[];
  filteredStatuses: string[];
  isSendable?: boolean;
  onCandidateSearch: (search: string) => void;
  openQRModal?: () => void;
  onSortChange: (options: any) => void;
  onFilterStatus: (statuses: string[]) => void;
  setPageSize: (size: number) => void;
  setCurrentPage: (page: number) => void;
  sortOptions: { column?: string; direction?: string };
  onInvitesRescind: (ids: number[]) => Promise<void>;
  onInvitesRescore: (ids: number[]) => void;
  onInviteResend: (id: number) => void;
  onInvitesDisableTimer: (invites: Array<IAssessmentInvite>) => void;
  onRenameCandidate: (
    userId: number,
    fullName: string,
    onRenameSuccess: () => void,
    onRenameFailure: () => void,
  ) => void;
}) {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { assessment } = useContext(AssessmentContext);
  const isAssessmentArchived = Boolean(assessment?.archivedOn);
  const assessmentAbility = useContext(AssessmentAbilityContext);
  const canManageAssessment = assessmentAbility.can(Action.Manage, Subject.ASSESSMENT);
  const canCreateAssessmentInvite = assessmentAbility.can(Action.Create, Subject.ASSESSMENT_INVITE);
  const canRescindAssessmentInvite = assessmentAbility.can(Action.Rescind, Subject.ASSESSMENT_INVITE);
  const canResendAssessmentInvite = assessmentAbility.can(Action.Resend, Subject.ASSESSMENT_INVITE);
  const canRescoreAssessmentInvite = assessmentAbility.can(Action.Rescore, Subject.ASSESSMENT_INVITE);
  const canRenameCandidate = assessmentAbility.can(Action.Update, Subject.ASSESSMENT_INVITE, "claimedByUser.fullName");
  const canViewAssessmentInviteTimer = assessmentAbility.can(Action.Read, Subject.ASSESSMENT_INVITE, "timerDisabled");
  const canUpdateAssessmentInviteTimer = assessmentAbility.can(
    Action.Update,
    Subject.ASSESSMENT_INVITE,
    "timerDisabled",
  );
  const canResetAssessmentInvite = assessmentAbility.can(Action.Reset, Subject.ASSESSMENT_INVITE);
  const canViewAssessmentInviteReports = assessmentAbility.can(Action.Read, Subject.ASSESSMENT_INVITE, "reports");
  const hasTimeLimit =
    assessment &&
    assessment.assessmentTests?.some((assessmentTest) =>
      assessmentTest.test.testElements.some((testElement) => {
        const timeLimit = testElement.config.startsSection?.timeLimit;
        return timeLimit && timeLimit > 0;
      }),
    );
  const handleSearchCandidate = async (value: string) => {
    if (!assessment) return;

    onCandidateSearch(value);
  };
  const [candidateToRename, setCandidateToRename] = useState<IAssessmentInvite | null>(null);

  const resetSearchCandidate = () => {
    onCandidateSearch("");
  };
  const [selectedInviteIds, setSelectedInviteIds] = useState<number[]>([]);
  const direction = sortOptions?.direction === "ASC" ? "ascend" : "descend";

  const languageColumn: ColumnType<IAssessmentInvite> = {
    key: "locale",
    title: "Language",
    dataIndex: "locale",
    className: style.tableColumn,
    render: (value: string | null) => getLocaleDisplayName(value) ?? "-",
  };

  const candidatesInvitesColumns: ColumnsType<IAssessmentInvite> = [
    ...candidatesInvitesColumnsStatic,
    ...(assessment?.template
      ? [
          {
            key: "overallCompositeScore",
            dataIndex: "overallCompositeScore",
            title: "Overall Score",
            className: classNames(style.tableColumn, style.overallColumn),
            sorter: true,
            defaultSortOrder:
              sortOptions?.column === "overallCompositeScore" && sortOptions?.direction
                ? (direction as SortOrder)
                : undefined,
            render: (value: number | null) => formatTableData(value ?? undefined, undefined, undefined, "-"),
          },
        ]
      : []),
    staticInvitedByColumn,
    languageColumn,
    {
      key: "actions",
      title: "",
      className: classNames(style.tableColumn, style.toLeft),
      render: (record: IAssessmentInvite) => {
        const showDisableTimer = hasTimeLimit && !record.timerDisabled && canUpdateAssessmentInviteTimer;
        const showTimerDisabled = hasTimeLimit && record.timerDisabled && canViewAssessmentInviteTimer;
        const showRescore = canRescoreAssessmentInvite && !isAssessmentArchived && !assessment?.template;
        const showInviteActions =
          showDisableTimer ||
          showTimerDisabled ||
          canRenameCandidate ||
          canResetAssessmentInvite ||
          showRescore ||
          canCreateAssessmentInvite ||
          canRescindAssessmentInvite ||
          canResendAssessmentInvite ||
          canViewAssessmentInviteReports;
        const isReportDisabled = record.status !== AssessmentInviteStatus.Done;

        return (
          <div className={classNames(style.actionsContainer, style.minActionsGap)}>
            {canManageAssessment && (
              <Tooltip overlay="Audit">
                <Link
                  to={`${pathname}/${record.inviteId}?${REPORT_MENU_ITEM}=audit`}
                  className={classNames(
                    buttonStyle.linkAsButton,
                    buttonStyle.linkAsButtonTransparent,
                    style.resendButton,
                  )}
                  aria-label={`View audit for ${record.candidateFullName}`}
                >
                  <AuditSVG />
                </Link>
              </Tooltip>
            )}

            {showInviteActions && (
              <Dropdown
                trigger={["click"]}
                placement="bottomRight"
                overlay={
                  <Menu>
                    {showDisableTimer && (
                      <Menu.Item
                        key="disable-timer"
                        disabled={
                          ![AssessmentInviteStatus.Invited, AssessmentInviteStatus.Accepted].includes(record.status)
                        }
                        onClick={() => onInvitesDisableTimer([record])}
                      >
                        Disable Timer
                      </Menu.Item>
                    )}
                    {canRenameCandidate && (
                      <Menu.Item
                        key="rename"
                        disabled={!record.claimedByUser?.userId}
                        onClick={() => setCandidateToRename(record)}
                      >
                        Rename
                      </Menu.Item>
                    )}
                    {canResetAssessmentInvite && (
                      <Menu.Item
                        key="reset-tests"
                        disabled={
                          ![
                            AssessmentInviteStatus.Started,
                            AssessmentInviteStatus.InProgress,
                            AssessmentInviteStatus.Done,
                          ].includes(record.status)
                        }
                      >
                        <Link
                          to={`${record.inviteId}/${ASSESSMENT_TEST_RESETS_ROUTE}`}
                          className={style.resetTestsMenuLink}
                        >
                          Reset Test(s)
                        </Link>
                      </Menu.Item>
                    )}
                    {canRescindAssessmentInvite && (
                      <Menu.Item
                        key="rescind"
                        disabled={[AssessmentInviteStatus.Done, AssessmentInviteStatus.Rescinded].includes(
                          record.status,
                        )}
                        onClick={() => onInvitesRescind([record.inviteId])}
                      >
                        Rescind
                      </Menu.Item>
                    )}
                    {showRescore && (
                      <Menu.Item
                        key="rescore"
                        disabled={record.status !== AssessmentInviteStatus.Done}
                        onClick={() => onInvitesRescore([record.inviteId])}
                      >
                        Rescore
                      </Menu.Item>
                    )}
                    {canResendAssessmentInvite && (
                      <Menu.Item
                        key="resend-invite"
                        onClick={() => onInviteResend(record.inviteId)}
                        disabled={
                          ![AssessmentInviteStatus.Invited, AssessmentInviteStatus.Accepted].includes(record.status)
                        }
                      >
                        Resend Invite
                      </Menu.Item>
                    )}
                    {showTimerDisabled && (
                      <Menu.Item key="timer-disabled" disabled={true}>
                        Timer Disabled
                      </Menu.Item>
                    )}
                  </Menu>
                }
              >
                <MoreOutlined style={{ fontSize: 24 }} />
              </Dropdown>
            )}

            {canViewAssessmentInviteReports && (
              <Link
                to={`${pathname}/${record.inviteId}`}
                className={classNames(
                  buttonStyle.linkAsButton,
                  buttonStyle.linkAsButtonTransparent,
                  style.resendButton,
                )}
                aria-disabled={isReportDisabled || undefined}
                onClick={isReportDisabled ? (e) => e.preventDefault() : undefined}
                aria-label={`View report for ${record.candidateFullName}`}
              >
                Report
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  candidatesInvitesColumns[0].filterDropdown = ({ confirm, selectedKeys, setSelectedKeys }) => {
    return (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          autoFocus
          placeholder="Search by candidate"
          value={String(selectedKeys[0] ?? "")}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          onPressEnter={() => {
            confirm();
            handleSearchCandidate((selectedKeys[0] as string) ?? "");
          }}
          style={{ marginBottom: 8, display: "block" }}
        />

        <Space>
          <Button
            colorType={ButtonColorType.dark}
            buttonSize={ButtonSize.small}
            icon={<SearchOutlined />}
            onClick={() => {
              confirm();
              handleSearchCandidate((selectedKeys[0] as string) ?? "");
            }}
          >
            Search
          </Button>

          <Button
            colorType={ButtonColorType.light}
            buttonSize={ButtonSize.small}
            onClick={() => {
              resetSearchCandidate();
              setSelectedKeys([]);
              confirm();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    );
  };
  candidatesInvitesColumns[0].defaultSortOrder =
    sortOptions?.column === "name" && sortOptions?.direction ? direction : undefined;
  candidatesInvitesColumns[0].defaultFilteredValue = [searchParams.get("candidate")].filter(Boolean) as string[];
  candidatesInvitesColumns[1].defaultSortOrder =
    sortOptions?.column === "email" && sortOptions?.direction ? direction : undefined;
  candidatesInvitesColumns[2].filters = statuses.map((status) => ({
    text: statusText[status],
    value: status,
  }));
  candidatesInvitesColumns[2].defaultFilteredValue = filteredStatuses.filter(Boolean);
  candidatesInvitesColumns[2].defaultSortOrder =
    sortOptions?.column === "status" && sortOptions?.direction ? direction : undefined;
  candidatesInvitesColumns[3].defaultSortOrder =
    sortOptions?.column === "createdAt" && sortOptions?.direction ? direction : undefined;
  candidatesInvitesColumns[4].defaultSortOrder =
    sortOptions?.column === "claimedOn" && sortOptions?.direction ? direction : undefined;
  candidatesInvitesColumns[5].defaultSortOrder =
    sortOptions?.column === "startedOn" && sortOptions?.direction ? direction : undefined;
  candidatesInvitesColumns[6].defaultSortOrder =
    sortOptions?.column === "finishedOn" && sortOptions?.direction ? direction : undefined;

  const isBulkRescindAvailable = selectedInviteIds
    .map((id) => dataList.find(({ inviteId }) => id === inviteId))
    .filter(
      (invite: IAssessmentInvite | undefined) =>
        invite && ![AssessmentInviteStatus.Rescinded, AssessmentInviteStatus.Done].includes(invite.status),
    ).length;

  const isBulkRescoreAvailable = selectedInviteIds.some((id) => {
    const invite = dataList.find(({ inviteId }) => id === inviteId);
    return invite && invite.status === AssessmentInviteStatus.Done;
  });

  return (
    <div className={style.tableBlock}>
      {candidateToRename && (
        <RenameUserModal
          candidateToRename={candidateToRename}
          onClose={() => setCandidateToRename(null)}
          onRenameCandidate={onRenameCandidate}
        />
      )}
      <TableHeader tableName={"Candidates"} hasSearchField={false}>
        <div className={style.tableButtonsBlock}>
          {canCreateAssessmentInvite && (
            <>
              <Button
                disabled={isAssessmentArchived || !isSendable}
                onClick={() => openQRModal && openQRModal()}
                colorType={ButtonColorType.transparentDark}
                buttonSize={ButtonSize.small}
              >
                Show QR
              </Button>
              <Button
                onClick={onAddCandidates}
                colorType={ButtonColorType.dark}
                buttonSize={ButtonSize.small}
                disabled={isAssessmentArchived || !isSendable}
                icon={<PlusSVG aria-hidden="true" />}
              >
                Add Manually
              </Button>
            </>
          )}
          {(canRescindAssessmentInvite || canRescoreAssessmentInvite) && (
            <Dropdown
              placement="bottomRight"
              trigger={["click"]}
              disabled={isAssessmentArchived}
              overlay={
                <Menu
                  items={[
                    canRescindAssessmentInvite
                      ? {
                          key: 0,
                          disabled: !isBulkRescindAvailable,
                          onClick: () => {
                            onInvitesRescind(selectedInviteIds);
                            setSelectedInviteIds([]);
                          },
                          label: "Rescind",
                        }
                      : null,
                    canRescoreAssessmentInvite
                      ? {
                          key: "rescore",
                          disabled: !isBulkRescoreAvailable,
                          onClick: () => {
                            onInvitesRescore(selectedInviteIds);
                            setSelectedInviteIds([]);
                          },
                          label: "Rescore",
                        }
                      : null,
                  ].filter(Boolean)}
                />
              }
            >
              <Button colorType={ButtonColorType.dark} buttonSize={ButtonSize.small}>
                Bulk Actions
              </Button>
            </Dropdown>
          )}
        </div>
      </TableHeader>
      <Table
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedInviteIds,
          onChange: (_selectedRowKeys, selectedRows: IAssessmentInvite[]) =>
            setSelectedInviteIds(selectedRows.map(({ inviteId }) => inviteId)),
        }}
        columns={candidatesInvitesColumns}
        dataSource={dataList}
        style={{ width: "100%" }}
        loading={loading}
        scroll={{ x: "1200px" }}
        onChange={(_pagination, filters, sorter: any) => {
          const direction =
            typeof sorter.order === "undefined" ? sorter.order : sorter.order === "ascend" ? "ASC" : "DESC";
          const column = sorter.columnKey === "candidateFullName" ? "name" : sorter.columnKey;

          onSortChange({ direction, column });
          onFilterStatus((filters.status as string[]) || []);
        }}
        pagination={{
          position: ["bottomLeft"],
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          current: currentPage,
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_current, size) => {
            setPageSize(size);
          },
        }}
      />
    </div>
  );
}
